import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Smartphone, Users, UsersRound, Megaphone, Send, ChevronDown, ChevronUp, X,
         Bot, Sparkles, CalendarDays, MessageSquareHeart, BellRing, Repeat } from 'lucide-react';
import { useT } from '../i18n';

const DISMISSED_KEY = 'disparaia_guia_dismissed';
const COLLAPSED_KEY = 'disparaia_guia_collapsed';

function storageGet(key, def) {
  try { const v = localStorage.getItem(key); return v === null ? def : v === 'true'; } catch { return def; }
}
function storageSet(key, val) {
  try { localStorage.setItem(key, String(val)); } catch {}
}


// A célula de etapa foi EXTRAÍDA daqui sem uma alteração de estilo: é o mesmo JSX,
// só movido, para a trilha poder ser renderizada mais de uma vez. Era a única forma
// de dar à concierge uma trilha PRÓPRIA sem duplicar 100 linhas de layout.
function Trilha({ steps, currentIdx, navigate, t }) {
  return (
      <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: 'max-content' }}>
        {steps.map((step, i) => {
          const isActive = currentIdx === i;

          return (
            <div key={step.id} style={{ display: 'flex', alignItems: 'flex-start' }}>
              {/* ── Step cell ── */}
              <div
                onClick={() => navigate(step.href)}
                title={step.desc}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  width: '130px', gap: '10px', cursor: 'pointer',
                }}
              >
                {/* Circle */}
                <div style={{ position: 'relative' }}>
                  {/* Glow ring for active step */}
                  {isActive && (
                    <div style={{
                      position: 'absolute', inset: '-6px', borderRadius: '50%',
                      background: `${step.corAtiva}20`,
                      animation: 'pulseRing 2s ease-in-out infinite',
                    }} />
                  )}
                  <div style={{
                    width: '60px', height: '60px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative', zIndex: 1,
                    background: step.done
                      ? 'linear-gradient(135deg, #bbf7d0, #dcfce7)'
                      : isActive
                      ? `linear-gradient(135deg, ${step.corAtiva}30, ${step.corAtiva}15)`
                      : 'var(--color-bg-soft)',
                    border: `2.5px solid ${step.done ? '#22c55e' : isActive ? step.corAtiva : 'var(--color-border)'}`,
                    color: step.done ? '#16a34a' : isActive ? step.corAtiva : '#9ca3af',
                    boxShadow: step.done
                      ? '0 2px 12px #22c55e30'
                      : isActive
                      ? `0 2px 16px ${step.corAtiva}40`
                      : 'none',
                    transition: 'all 0.3s ease',
                  }}>
                    {step.done
                      ? <Check size={26} strokeWidth={2.5} />
                      : step.icon
                    }
                  </div>

                  {/* Number badge */}
                  <div style={{
                    position: 'absolute', top: '-4px', right: '-4px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    background: step.done ? '#22c55e' : isActive ? step.corAtiva : '#d1d5db',
                    color: '#fff', fontSize: '10px', fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid var(--color-surface)', zIndex: 2,
                  }}>
                    {step.num}
                  </div>
                </div>

                {/* Text */}
                <div style={{ textAlign: 'center', padding: '0 4px' }}>
                  <div style={{
                    fontSize: '12px', fontWeight: 600, lineHeight: 1.3,
                    color: step.done ? '#16a34a' : isActive ? 'var(--color-text)' : 'var(--color-text-muted)',
                  }}>
                    {step.label}
                    {step.optional && (
                      <span style={{ display: 'block', fontSize: '10px', fontWeight: 400, opacity: 0.65 }}>{t('fluxo.opcional')}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '3px', lineHeight: 1.35 }}>
                    {step.desc}
                  </div>
                  {/* CTA link */}
                  <div style={{
                    fontSize: '11px', marginTop: '5px', fontWeight: 600,
                    color: step.done ? '#16a34a' : isActive ? step.corAtiva : 'var(--color-text-muted)',
                    textDecoration: 'none',
                  }}>
                    {step.done ? t('fluxo.concluido') : isActive ? t('fluxo.fazer_agora') : t('fluxo.pendente')}
                  </div>
                </div>
              </div>

              {/* ── Connector line ── */}
              {i < steps.length - 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', marginTop: '28px', flexShrink: 0, width: '40px',
                }}>
                  <div style={{
                    height: '2px', width: '100%',
                    background: step.done
                      ? 'linear-gradient(90deg, #22c55e, #22c55e)'
                      : `linear-gradient(90deg, var(--color-border), var(--color-border))`,
                    transition: 'background 0.4s ease',
                  }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
  );
}

// `setupConcierge` é OPCIONAL de propósito: sem a prop, o componente renderiza
// exatamente a trilha de campanhas que renderizava antes. A mudança é aditiva, e
// é isso que torna seguro mexer num cartão que todo cliente vê no Dashboard.
export default function FluxoInicio({ instanciasCount, contatosCount, gruposCount, campanhasCount, campanhasEnviadas, setupConcierge }) {
  const navigate = useNavigate();
  const { t } = useT();
  const [dismissed, setDismissed] = useState(() => storageGet(DISMISSED_KEY, false));
  const [collapsed, setCollapsed] = useState(() => storageGet(COLLAPSED_KEY, false));

  if (dismissed) return null;

  // A trilha da concierge. Cada etapa só existe porque há um sinal REAL do servidor
  // para ela (`GET /ia/setup`) — etapa de onboarding que não sabe se está pronta é
  // pior que etapa nenhuma: ou mostra "concluído" sem estar, ou cobra para sempre.
  //
  // ⚠️ As três primeiras são obrigatórias e o backend é a fonte dessa regra
  // (`OBRIGATORIAS`), travada por invariante nas duas pontas. A agenda só entrou
  // depois de RENDERIZAR a tela: com ela opcional, todo pendente da trilha era
  // opcional, `findIndex` devolvia -1 e a trilha ficava sem passo ativo — sem
  // "Fazer agora", sem anel pulsante —, inerte ao lado da de campanhas.
  const sc = setupConcierge;
  const trilhaConcierge = !sc ? [] : [
    {
      id: 'ia-ligada', num: 1, icon: <Bot size={22} />,
      label: t('fluxo.c1_label'), desc: t('fluxo.c1_desc'),
      href: '/ia-config', done: sc.ia_ligada === true, corAtiva: '#16a34a',
    },
    {
      id: 'ia-persona', num: 2, icon: <Sparkles size={22} />,
      label: t('fluxo.c2_label'), desc: t('fluxo.c2_desc'),
      href: '/ia-config', done: sc.com_persona === true, corAtiva: '#16a34a',
    },
    {
      id: 'ia-agenda', num: 3, icon: <CalendarDays size={22} />,
      label: t('fluxo.c3_label'), desc: t('fluxo.c3_desc'),
      href: '/ia-config', done: sc.com_agenda === true, corAtiva: '#16a34a',
    },
    {
      id: 'ia-perfil', num: 4, icon: <MessageSquareHeart size={22} />,
      label: t('fluxo.c4_label'), desc: t('fluxo.c4_desc'),
      href: '/ia-config', done: sc.com_perfil === true, optional: true, corAtiva: '#13305f',
    },
    {
      id: 'ia-lembretes', num: 5, icon: <BellRing size={22} />,
      label: t('fluxo.c5_label'), desc: t('fluxo.c5_desc'),
      href: '/ia-config', done: sc.com_lembretes === true, optional: true, corAtiva: '#13305f',
    },
    {
      id: 'ia-acompanhamento', num: 6, icon: <Repeat size={22} />,
      label: t('fluxo.c6_label'), desc: t('fluxo.c6_desc'),
      href: '/ia-config', done: sc.com_acompanhamento === true, optional: true, corAtiva: '#13305f',
    },
  ];
  const idxConcierge = trilhaConcierge.findIndex(s2 => !s2.done && !s2.optional);

  const steps = [
    {
      id: 'whatsapp',
      num: 1,
      icon: <Smartphone size={22} />,
      label: t('fluxo.step1_label'),
      desc: t('fluxo.step1_desc'),
      href: '/whatsapp',
      done: instanciasCount > 0,
      corAtiva: '#16a34a',
    },
    {
      id: 'contatos',
      num: 2,
      icon: <Users size={22} />,
      label: t('fluxo.step2_label'),
      desc: t('fluxo.step2_desc'),
      href: '/contatos',
      done: contatosCount > 0,
      corAtiva: '#13305f',
    },
    {
      id: 'grupos',
      num: 3,
      icon: <UsersRound size={22} />,
      label: t('fluxo.step3_label'),
      desc: t('fluxo.step3_desc'),
      href: '/contatos',
      done: gruposCount > 0,
      optional: true,
      corAtiva: '#13305f',
    },
    {
      id: 'campanha',
      num: 4,
      icon: <Megaphone size={22} />,
      label: t('fluxo.step4_label'),
      desc: t('fluxo.step4_desc'),
      href: '/campanhas/nova',
      done: campanhasCount > 0,
      corAtiva: '#ec4899',
    },
    {
      id: 'enviar',
      num: 5,
      icon: <Send size={22} />,
      label: t('fluxo.step5_label'),
      desc: t('fluxo.step5_desc'),
      href: '/campanhas',
      done: campanhasEnviadas > 0,
      corAtiva: '#22c55e',
    },
  ];

  const currentIdx = steps.findIndex(s => !s.done && !s.optional);
  // O progresso do cabeçalho conta as DUAS trilhas, senão o cliente que acabou de
  // configurar a concierge veria o contador parado e concluiria que não salvou.
  const todas = [...trilhaConcierge, ...steps];
  const doneCt = todas.filter(s => s.done).length;
  const allDone = todas.every(s => s.done || s.optional);
  const pct = Math.round((doneCt / todas.length) * 100);

  return (
    <div className="card" style={{ marginBottom: '1.5rem', overflow: 'hidden' }}>
      {/* ── Header ── */}
      <div
        onClick={() => { const next = !collapsed; setCollapsed(next); storageSet(COLLAPSED_KEY, next); }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 18px', cursor: 'pointer', userSelect: 'none',
          borderBottom: collapsed ? 'none' : '1px solid var(--color-divider)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <span style={{ fontSize: '18px', flexShrink: 0 }}>🗺️</span>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontWeight: 600, fontSize: '14px' }}>{t('fluxo.titulo')}</span>
            <span className="muted" style={{ fontSize: '12px', marginLeft: '8px' }}>
              {t('fluxo.etapas_concluidas', { feitas: doneCt, total: todas.length })}
            </span>
          </div>
          {/* Mini progress bar */}
          <div style={{ width: '100px', height: '5px', background: 'var(--color-border)', borderRadius: '3px', overflow: 'hidden', flexShrink: 0 }}>
            <div style={{
              width: `${pct}%`, height: '100%', borderRadius: '3px',
              background: allDone ? '#22c55e' : 'linear-gradient(90deg,#16a34a,#13305f)',
              transition: 'width 0.5s ease'
            }} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          {allDone && (
            <button
              onClick={e => { e.stopPropagation(); setDismissed(true); storageSet(DISMISSED_KEY, true); }}
              className="btn btn-sm btn-secondary" style={{ fontSize: '11px', padding: '3px 8px' }}
            >
              {t('fluxo.fechar_guia')}
            </button>
          )}
          <button
            onClick={e => { e.stopPropagation(); setDismissed(true); storageSet(DISMISSED_KEY, true); }}
            className="btn btn-icon btn-sm btn-secondary" title={t('fluxo.fechar')}
          >
            <X size={13} />
          </button>
          {collapsed ? <ChevronDown size={16} color="var(--color-text-muted)" /> : <ChevronUp size={16} color="var(--color-text-muted)" />}
        </div>
      </div>

      {/* ── Steps ── */}
      {!collapsed && (
        <div style={{ padding: '28px 24px 24px', overflowX: 'auto' }}>
          {trilhaConcierge.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', marginBottom: 14, letterSpacing: '.02em' }}>
                {t('fluxo.trilha_concierge')}
              </div>
              <Trilha steps={trilhaConcierge} currentIdx={idxConcierge} navigate={navigate} t={t} />
              <div style={{ height: 1, background: 'var(--color-divider)', margin: '26px 0 20px' }} />
              <div style={{ fontSize: 12, fontWeight: 700, color: '#13305f', marginBottom: 14, letterSpacing: '.02em' }}>
                {t('fluxo.trilha_campanhas')}
              </div>
            </>
          )}
          <Trilha steps={steps} currentIdx={currentIdx} navigate={navigate} t={t} />

          {/* All done message */}
          {allDone && (
            <div style={{
              marginTop: '16px', padding: '10px 16px', borderRadius: 'var(--radius)',
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              fontSize: '13px', color: '#16a34a', fontWeight: 500, textAlign: 'center'
            }}>
              {t('fluxo.tudo_pronto')}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes pulseRing {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.12); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}
